import { useEffect, useState } from 'react'
import type { LessonProcessDetailView } from '@/types/coaching'

function hasIncompleteReviewAttempt(lesson: LessonProcessDetailView): boolean {
  if (lesson.lessonKind !== 'designated_review') return false
  const reviewComponents = lesson.reviewComponents ?? []
  if (reviewComponents.length === 0) return lesson.status !== 'completed'
  return reviewComponents.some((component) => component.status !== 'completed')
}

export function pickDefaultLessonId(
  lessons: LessonProcessDetailView[],
  pendingReviewLessonIds: number[] = [],
): number | null {
  if (lessons.length === 0) return null

  const pendingReviewIdSet = new Set(pendingReviewLessonIds)
  const pendingReview = lessons
    .filter((l) => pendingReviewIdSet.has(l.lessonId))
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  if (pendingReview[0]) return pendingReview[0].lessonId

  const designatedReview = lessons
    .filter(hasIncompleteReviewAttempt)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  if (designatedReview[0]) return designatedReview[0].lessonId

  const inProgress = lessons
    .filter((l) => l.status === 'in_progress')
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  if (inProgress[0]) return inProgress[0].lessonId

  const completed = lessons
    .filter((l) => l.status === 'completed' && l.completedAt)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
  if (completed[0]) return completed[0].lessonId

  const byRecent = [...lessons].sort((a, b) =>
    b.startedAt.localeCompare(a.startedAt),
  )
  return byRecent[0]?.lessonId ?? null
}

/**
 * Owns the focus-lesson selection for the learning progress section.
 * Recomputes the default whenever the lessons list identity changes
 * (e.g. learner switch). External callers may override via `setFocusLessonId`.
 */
export function useFocusLesson(lessons: LessonProcessDetailView[]) {
  const [focusLessonId, setFocusLessonId] = useState<number | null>(() =>
    pickDefaultLessonId(lessons),
  )

  useEffect(() => {
    setFocusLessonId(pickDefaultLessonId(lessons))
  }, [lessons])

  return { focusLessonId, setFocusLessonId }
}
